import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import FormSection from '../../components/FormSection';
import { useAuth } from '../../contexts/AuthContext';
import { OrderProduct, OrderCategory, OrderSubcategory, ProductFieldDefinition } from '../../types/order';

const ADMIN_API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function humanizeFieldKey(key: string): string {
  if (!key) return '';
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function productFieldRowLabel(f: ProductFieldDefinition): string {
  const override = (f.label_override || '').trim();
  if (override) return override;
  const def = (f.default_display_label || '').trim();
  if (def) return def;
  return humanizeFieldKey(f.field_key);
}

function normalizePlanFeaturesRecord(obj: unknown): Record<string, string> {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    out[k] = typeof v === 'string' ? v : String(v);
  }
  return out;
}

function parsePlanFeaturesJson(raw: string): Record<string, string> | null {
  const trimmed = raw.trim() || '{}';
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return normalizePlanFeaturesRecord(parsed);
}

/** Merge legacy product columns into plan_features for editing when keys missing. */
function hydratePlanFeaturesFromProduct(product: OrderProduct): Record<string, string> {
  const pf = normalizePlanFeaturesRecord(product.plan_features);
  const setIf = (key: string, val: string | null | undefined) => {
    const s = val != null ? String(val).trim() : '';
    if (!s || Object.prototype.hasOwnProperty.call(pf, key)) return;
    pf[key] = s;
  };
  setIf('speed_tier', product.speed_tier);
  setIf('data_cap', product.data_cap);
  setIf('typical_evening_speed', product.typical_evening_speed);
  setIf('contract_terms', product.contract_terms);
  setIf('features', product.features);
  if (product.max_channels != null && !Object.prototype.hasOwnProperty.call(pf, 'max_channels')) {
    pf.max_channels = String(product.max_channels);
  }
  setIf('available_technology_types', product.available_technology_types ?? undefined);
  setIf('customer_type', product.customer_type ?? undefined);
  return pf;
}

function ProductsPage() {
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<OrderSubcategory[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<OrderSubcategory[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [filterSubcategoryId, setFilterSubcategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Cost is admin-only data, served from admin-only endpoints (never part of the product payload)
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [costInput, setCostInput] = useState('');
  const [originalCost, setOriginalCost] = useState('');
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<OrderProduct | null>(null);
  const [cisFile, setCisFile] = useState<File | null>(null);
  const [productImageFile, setProductImageFile] = useState<File | null>(null);
  const [productImageUploading, setProductImageUploading] = useState(false);
  const [productImagePreviewKey, setProductImagePreviewKey] = useState(0);
  const productImageInputRef = useRef<HTMLInputElement>(null);
  /** Merged plan_features (def keys + extra keys) for internet_plan when subcategory is set. */
  const [planFeatures, setPlanFeatures] = useState<Record<string, string>>({});
  /** JSON object for keys not present in field definitions. */
  const [planFeaturesExtrasJson, setPlanFeaturesExtrasJson] = useState('{}');
  const [productFieldDefinitions, setProductFieldDefinitions] = useState<ProductFieldDefinition[]>([]);
  const [productFieldsLoading, setProductFieldsLoading] = useState(false);
  /** Raw JSON for entire plan_features when no field definitions for category. */
  const [planFeaturesJsonNoSubcategory, setPlanFeaturesJsonNoSubcategory] = useState('{}');
  const extrasJsonEditorFocused = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    sku: '',
    product_type: 'internet_plan' as 'internet_plan' | 'equipment' | 'voice_plan',
    category_id: '',
    subcategory_id: '',
    monthly_price: '',
    setup_price: '',
    one_time_price: '',
    is_active: true,
    is_available: true,
    display_order: 0,
    service_type_code: '',
    terms_subcategory_id: '',
    supa_tariff_id: '',
  });

  const [supaPlans, setSupaPlans] = useState<{ planName: string; tariffID: string; serviceType: string; monthlyFee: string }[]>([]);
  const [supaPlansLoading, setSupaPlansLoading] = useState(false);
  const [supaPlansError, setSupaPlansError] = useState<string | null>(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    if (formData.category_id) {
      loadSubcategories(formData.category_id);
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [productsData, categoriesData, subcategoriesData, typesData] = await Promise.all([
        api.adminListProducts(),
        api.adminListCategories(),
        api.adminListSubcategories(), // all subcategories for lookup and filter
        api.getServiceTypes(false),
      ]);

      // Normalize product data - convert string prices to numbers
      const normalizedProducts = productsData.map((p: any) => ({
        ...p,
        monthly_price: p.monthly_price ? parseFloat(String(p.monthly_price)) : undefined,
        setup_price: p.setup_price ? parseFloat(String(p.setup_price)) : undefined,
        one_time_price: p.one_time_price ? parseFloat(String(p.one_time_price)) : undefined,
        plan_features:
          p.plan_features && typeof p.plan_features === 'object' && !Array.isArray(p.plan_features)
            ? p.plan_features
            : {},
      }));

      setProducts(normalizedProducts);
      setCategories(categoriesData);
      setAllSubcategories(subcategoriesData);
      setServiceTypes(
        (typesData || [])
          .slice()
          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      );

    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadSubcategories = async (categoryId: string) => {
    try {
      const subcats = await api.adminListSubcategories(categoryId);
      setSubcategories(subcats);
    } catch (err: any) {
      console.error('Failed to load subcategories', err);
    }
  };

  type ProductTypeSlug = 'internet_plan' | 'equipment' | 'voice_plan';

  /** When category is set and slug matches, return API product_type; otherwise undefined so caller can keep existing form value. */
  const deriveProductTypeFromCategoryId = (categoryId: string): ProductTypeSlug | undefined => {
    if (!categoryId) return undefined;
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return undefined;
    const slug = cat.slug?.toLowerCase();
    if (slug === 'internet' || slug === 'mobile') return 'internet_plan';
    if (slug === 'equipment') return 'equipment';
    if (slug === 'voice') return 'voice_plan';
    return undefined;
  };

  const recomputeExtrasJsonString = useCallback((pf: Record<string, string>, fields: ProductFieldDefinition[]) => {
    const defKeys = new Set(fields.map((f) => f.field_key));
    const extras = Object.fromEntries(Object.entries(pf).filter(([k]) => !defKeys.has(k)));
    return JSON.stringify(extras, null, 2);
  }, []);

  const handlePlanFeaturesExtrasJsonChange = useCallback(
    (raw: string) => {
      setPlanFeaturesExtrasJson(raw);
      const parsed = parsePlanFeaturesJson(raw);
      if (!parsed) return;
      const defKeys = new Set(productFieldDefinitions.map((f) => f.field_key));
      setPlanFeatures((prev) => {
        const next: Record<string, string> = {};
        for (const f of productFieldDefinitions) {
          const k = f.field_key;
          if (Object.prototype.hasOwnProperty.call(prev, k)) next[k] = prev[k] ?? '';
        }
        for (const [k, v] of Object.entries(parsed)) {
          if (!defKeys.has(k)) next[k] = v;
        }
        return next;
      });
    },
    [productFieldDefinitions],
  );

  const handlePlanFeaturesExtrasBlur = useCallback(() => {
    extrasJsonEditorFocused.current = false;
    const parsed = parsePlanFeaturesJson(planFeaturesExtrasJson);
    if (!parsed) return;
    const defKeys = new Set(productFieldDefinitions.map((f) => f.field_key));
    setPlanFeatures((prev) => {
      const next: Record<string, string> = {};
      for (const f of productFieldDefinitions) {
        const k = f.field_key;
        if (Object.prototype.hasOwnProperty.call(prev, k)) next[k] = prev[k] ?? '';
      }
      for (const [k, v] of Object.entries(parsed)) {
        if (!defKeys.has(k)) next[k] = v;
      }
      return next;
    });
  }, [planFeaturesExtrasJson, productFieldDefinitions]);

  useEffect(() => {
    if (!dialogOpen || !formData.category_id) {
      if (!dialogOpen) {
        setProductFieldDefinitions([]);
        setProductFieldsLoading(false);
      }
      return;
    }
    let cancelled = false;
    setProductFieldsLoading(true);
    (async () => {
      try {
        const rows = await api.adminResolveProductFieldDefinitions(
          formData.category_id,
          formData.subcategory_id || undefined,
        );
        if (cancelled) return;
        const sorted = [...(rows || [])].sort((a, b) => a.display_order - b.display_order);
        setProductFieldDefinitions(sorted);
      } catch (err: any) {
        if (!cancelled) {
          console.error(err);
          setProductFieldDefinitions([]);
          setError(err?.message || 'Failed to load product field definitions');
        }
      } finally {
        if (!cancelled) setProductFieldsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, formData.category_id, formData.subcategory_id]);

  useEffect(() => {
    if (!dialogOpen || extrasJsonEditorFocused.current) return;
    setPlanFeaturesExtrasJson(recomputeExtrasJsonString(planFeatures, productFieldDefinitions));
  }, [dialogOpen, planFeatures, productFieldDefinitions, recomputeExtrasJsonString]);

  const storedKeysOutsideDefinition = useMemo(() => {
    if (!dialogOpen || productFieldDefinitions.length === 0) return [];
    const def = new Set(productFieldDefinitions.map((f) => f.field_key));
    return Object.keys(planFeatures).filter((k) => !def.has(k) && String(planFeatures[k] ?? '').trim() !== '');
  }, [dialogOpen, productFieldDefinitions, planFeatures]);
  
  const handleOpenDialog = (product?: OrderProduct) => {
    setCostInput('');
    setOriginalCost('');
    setCostError(null);
    if (isAdmin && product) {
      setCostLoading(true);
      api
        .getProductCost(product.id)
        .then((r) => {
          const v = r.monthly_cost != null ? Number(r.monthly_cost).toFixed(2) : '';
          setCostInput(v);
          setOriginalCost(v);
        })
        .catch(() => setCostError('Could not load the cost for this product.'))
        .finally(() => setCostLoading(false));
    }
    setProductImageFile(null);
    if (productImageInputRef.current) productImageInputRef.current.value = '';
    if (product) {
      setEditingProduct(product);
      setProductImagePreviewKey((k) => k + 1);
      setFormData({
        name: product.name,
        slug: product.slug,
        description: product.description || '',
        sku: product.sku || '',
        product_type: product.product_type,
        category_id: product.category_id || '',
        subcategory_id: product.subcategory_id || '',
        monthly_price: product.monthly_price?.toString() || '',
        setup_price: product.setup_price?.toString() || '',
        one_time_price: product.one_time_price?.toString() || '',
        is_active: product.is_active,
        is_available: product.is_available,
        display_order: product.display_order || 0,
        service_type_code: product.service_type_code || '',
        terms_subcategory_id: product.terms_subcategory_id || '',
        supa_tariff_id: product.supa_tariff_id || '',
      });
      setSupaPlans([]);
      setSupaPlansError(null);
      const merged = hydratePlanFeaturesFromProduct(product);
      setPlanFeatures(merged);
      setPlanFeaturesExtrasJson('{}');
      setPlanFeaturesJsonNoSubcategory(JSON.stringify(merged, null, 2));
      if (product.category_id) {
        loadSubcategories(product.category_id);
      }
    } else {
      setEditingProduct(null);
      setProductImagePreviewKey((k) => k + 1);
      setFormData({
        name: '',
        slug: '',
        description: '',
        sku: '',
        product_type: 'internet_plan',
        category_id: '',
        subcategory_id: '',
        monthly_price: '',
        setup_price: '',
        one_time_price: '',
        is_active: true,
        is_available: true,
        display_order: 0,
        service_type_code: '',
        terms_subcategory_id: '',
        supa_tariff_id: '',
      });
      setSupaPlans([]);
      setSupaPlansError(null);
      setSubcategories([]);
      setPlanFeatures({});
      setPlanFeaturesExtrasJson('{}');
      setPlanFeaturesJsonNoSubcategory('{}');
    }
    extrasJsonEditorFocused.current = false;
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setCisFile(null);
    setProductImageFile(null);
    setSupaPlans([]);
    setSupaPlansError(null);
    setProductImagePreviewKey((k) => k + 1);
    setPlanFeatures({});
    setPlanFeaturesExtrasJson('{}');
    setPlanFeaturesJsonNoSubcategory('{}');
    setProductFieldDefinitions([]);
    extrasJsonEditorFocused.current = false;
  };

  const productImagePublicUrl = (productId: string, cacheBust?: number | string) => {
    const base = `${ADMIN_API_BASE.replace(/\/$/, '')}/api/orders/product-images/${productId}`;
    if (cacheBust === undefined || cacheBust === '') return base;
    return `${base}?v=${encodeURIComponent(String(cacheBust))}`;
  };

  const handleProductImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductImageFile(e.target.files?.[0] ?? null);
  };

  const handleUploadProductImageNow = async () => {
    if (!editingProduct || !productImageFile) return;
    setProductImageUploading(true);
    setError(null);
    try {
      const updated = await api.adminUploadProductImage(editingProduct.id, productImageFile);
      setEditingProduct((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      setProductImageFile(null);
      if (productImageInputRef.current) productImageInputRef.current.value = '';
      setProductImagePreviewKey((k) => k + 1);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to upload product image');
    } finally {
      setProductImageUploading(false);
    }
  };

  const handleRemoveProductImage = async () => {
    if (!editingProduct?.image_path) return;
    if (!window.confirm('Remove this product image?')) return;
    setProductImageUploading(true);
    setError(null);
    try {
      const updated = await api.adminDeleteProductImage(editingProduct.id);
      setEditingProduct((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      setProductImagePreviewKey((k) => k + 1);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to remove product image');
    } finally {
      setProductImageUploading(false);
    }
  };

  const handleLoadSupaPlans = async () => {
    setSupaPlansLoading(true);
    setSupaPlansError(null);
    setSupaPlans([]);
    try {
      const result = await api.adminSupaPlanList();
      if (result.availablePlans && result.availablePlans.length > 0) {
        setSupaPlans(result.availablePlans);
      } else {
        setSupaPlansError('No plans returned from Supa Networks API.');
      }
    } catch (err: any) {
      console.error('Supa plan list error:', err);
      setSupaPlansError(err?.response?.data?.detail || err?.message || 'Failed to load Supa plans');
    } finally {
      setSupaPlansLoading(false);
    }
  };

  const selectedSubcategorySlug = subcategories.find((s) => s.id === formData.subcategory_id)?.slug ?? '';

  // Live margin preview: catalogue retail is GST-inclusive, cost is ex GST
  const marginPreview = (() => {
    const retailInc = parseFloat(formData.monthly_price);
    const cost = parseFloat(costInput);
    if (!Number.isFinite(retailInc) || !Number.isFinite(cost)) return null;
    const retailEx = Math.round((retailInc / 1.1) * 100) / 100;
    const margin = Math.round((retailEx - cost) * 100) / 100;
    return { retailEx, margin, pct: retailEx > 0 ? (margin / retailEx) * 100 : null };
  })();
  const showCisUpload =
    selectedSubcategorySlug?.toLowerCase() === 'nbn' ||
    selectedSubcategorySlug?.toLowerCase() === 'supa' ||
    selectedSubcategorySlug?.toLowerCase() === 'voice-residential';
  
  const handleSave = async () => {
    try {
      setError(null);

      const costValue = costInput.trim();
      if (isAdmin && costValue && !/^\d{1,5}(\.\d{1,2})?$/.test(costValue)) {
        setCostError('Enter the cost as a dollar amount, e.g. 60.00');
        return;
      }
      setCostError(null);

      const resolvedProductTypeSave = deriveProductTypeFromCategoryId(formData.category_id) ?? formData.product_type;

      let planFeaturesOut: Record<string, string> | undefined;
      if (formData.category_id && productFieldDefinitions.length > 0) {
        const extrasParsed = parsePlanFeaturesJson(planFeaturesExtrasJson);
        if (!extrasParsed) {
          setError('Extra plan feature keys must be valid JSON (object with string keys and string values).');
          return;
        }
        const defKeys = new Set(productFieldDefinitions.map((f) => f.field_key));
        planFeaturesOut = {};
        for (const f of productFieldDefinitions) {
          planFeaturesOut[f.field_key] = planFeatures[f.field_key] ?? '';
        }
        for (const [k, v] of Object.entries(extrasParsed)) {
          if (!defKeys.has(k)) planFeaturesOut[k] = v;
        }
      } else if (formData.category_id) {
        const parsed = parsePlanFeaturesJson(planFeaturesJsonNoSubcategory);
        if (!parsed) {
          setError('Product fields (JSON) must be valid JSON (object with string keys and string values).');
          return;
        }
        planFeaturesOut = parsed;
      }

      const pf = planFeaturesOut;

      const productData: any = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description || undefined,
        sku: formData.sku || undefined,
        product_type: resolvedProductTypeSave,
        category_id: formData.category_id || undefined,
        subcategory_id: formData.subcategory_id || undefined,
        terms_subcategory_id: formData.terms_subcategory_id ? formData.terms_subcategory_id : null,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : undefined,
        setup_price: formData.setup_price ? parseFloat(formData.setup_price) : undefined,
        one_time_price: formData.one_time_price ? parseFloat(formData.one_time_price) : undefined,
        is_active: formData.is_active,
        is_available: formData.is_available,
        display_order: formData.display_order,
        service_type_code:
          (formData.service_type_code || '').trim() ||
          (editingProduct ? null : undefined),
        supa_tariff_id: (formData.supa_tariff_id || '').trim() || null,
      };

      if (pf) {
        productData.plan_features = pf;
        productData.speed_tier = pf.speed_tier?.trim() || undefined;
        productData.data_cap = pf.data_cap?.trim() || undefined;
        productData.typical_evening_speed = pf.typical_evening_speed?.trim() || undefined;
        productData.contract_terms = pf.contract_terms?.trim() || undefined;
        productData.features = pf.features?.trim() || undefined;
        if (pf.max_channels?.trim()) {
          const n = parseInt(pf.max_channels.trim(), 10);
          productData.max_channels = Number.isFinite(n) ? n : undefined;
        }
        productData.available_technology_types = pf.available_technology_types?.trim() || undefined;
        productData.customer_type = pf.customer_type?.trim() || undefined;
      }

      let productId: string;
      if (editingProduct) {
        await api.adminUpdateProduct(editingProduct.id, productData);
        productId = editingProduct.id;
      } else {
        const created = await api.adminCreateProduct(productData);
        productId = created.id;
      }

      if (showCisUpload && cisFile) {
        await api.adminUploadCriticalInfoSummary(productId, cisFile);
      }

      if (productImageFile) {
        await api.adminUploadProductImage(productId, productImageFile);
      }

      // Cost is saved separately (admin-only endpoint). A failure here must not lose the product
      // that was just saved/created, so it is reported after the dialog closes.
      let costSaveError: string | null = null;
      if (isAdmin && costValue !== originalCost) {
        try {
          await api.setProductCost(productId, costValue || null);
        } catch (costErr: any) {
          const detail = costErr?.response?.data?.detail;
          costSaveError = typeof detail === 'string' ? detail : 'the request failed';
        }
      }

      // Close dialog first
      handleCloseDialog();
      
      // Reload data - if this fails, at least the dialog is closed
      try {
        await loadData();
      } catch (loadErr: any) {
        console.error('Error reloading products after save:', loadErr);
        setError('Product saved, but failed to refresh list. Please refresh the page.');
      }
      if (costSaveError) {
        setError(`Product saved, but the cost could not be saved (${costSaveError}). Edit the product to set it.`);
      }
      
    } catch (err: any) {
      console.error('Error saving product:', err);
      setError(err.message || 'Failed to save product');
    }
  };
  
  const handleDelete = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    try {
      await api.adminDeleteProduct(productId);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete product');
    }
  };
  
  const formatPrice = (price?: number) => {
    if (!price) return 'N/A';
    return `$${price.toFixed(2)}`;
  };
  
  const getCategoryName = (categoryId?: string) =>
    categoryId ? categories.find((c) => c.id === categoryId)?.name ?? '—' : '—';
  const getSubcategoryName = (subcategoryId?: string) =>
    subcategoryId ? allSubcategories.find((s) => s.id === subcategoryId)?.name ?? '—' : '—';

  const serviceTypeLabel = (code?: string | null) => {
    if (!code) return '—';
    const st = serviceTypes.find((t: any) => t.code === code);
    return st ? `${st.name} (${code})` : code;
  };

  const filteredProducts = products.filter((p) => {
    if (filterCategoryId && p.category_id !== filterCategoryId) return false;
    if (filterSubcategoryId && p.subcategory_id !== filterSubcategoryId) return false;
    return true;
  });

  const filterSubcategoryOptions = filterCategoryId
    ? allSubcategories.filter((s) => s.category_id === filterCategoryId)
    : allSubcategories;

function normalizeProductFieldOptions(raw: unknown): { value: string; label: string }[] {
  if (!Array.isArray(raw)) return [];
  const out: { value: string; label: string }[] = [];
  for (const it of raw) {
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      const o = it as Record<string, unknown>;
      const value = String(o.value ?? '').trim();
      const label = String(o.label ?? o.value ?? '').trim() || value;
      if (value) out.push({ value, label });
    }
  }
  return out;
}

  const renderProductFieldInput = (f: ProductFieldDefinition) => {
    const val = planFeatures[f.field_key] ?? '';
    const label = productFieldRowLabel(f);
    const ft = (f.field_type || 'single_line_text').toLowerCase();
    const opts = normalizeProductFieldOptions(f.field_options);

    if (ft === 'checkbox') {
      const checked = val === 'true' || val === '1' || String(val).toLowerCase() === 'yes';
      return (
        <FormControlLabel
          key={f.id}
          sx={{ mb: 2, display: 'block' }}
          control={
            <Checkbox
              checked={checked}
              onChange={(e) =>
                setPlanFeatures((prev) => ({
                  ...prev,
                  [f.field_key]: e.target.checked ? 'true' : '',
                }))
              }
            />
          }
          label={label}
        />
      );
    }

    if (ft === 'checkboxes') {
      const arr = val ? val.split(',').map((s) => s.trim()).filter(Boolean) : [];
      const toggle = (optVal: string, checked: boolean) => {
        const next = checked
          ? [...arr.filter((t) => t !== optVal), optVal]
          : arr.filter((t) => t !== optVal);
        setPlanFeatures((prev) => ({ ...prev, [f.field_key]: next.join(',') }));
      };
      const choiceOpts = opts.length > 0 ? opts : [];
      if (choiceOpts.length === 0) {
        return (
          <TextField
            key={f.id}
            fullWidth
            sx={{ mb: 2 }}
            label={label}
            value={val}
            helperText="Define options for this field in Category Management."
            onChange={(e) => setPlanFeatures((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
          />
        );
      }
      return (
        <Box key={f.id} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {choiceOpts.map((o) => (
              <FormControlLabel
                key={o.value}
                control={
                  <Checkbox
                    size="small"
                    checked={arr.includes(o.value)}
                    onChange={(e) => toggle(o.value, e.target.checked)}
                  />
                }
                label={o.label || o.value}
              />
            ))}
          </Box>
        </Box>
      );
    }

    if (ft === 'dropdown') {
      if (opts.length === 0) {
        return (
          <TextField
            key={f.id}
            fullWidth
            sx={{ mb: 2 }}
            label={label}
            value={val}
            helperText="Define options for this field in Category Management."
            onChange={(e) => setPlanFeatures((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
          />
        );
      }
      return (
        <FormControl key={f.id} fullWidth sx={{ mb: 2 }}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={val}
            label={label}
            onChange={(e) =>
              setPlanFeatures((prev) => ({ ...prev, [f.field_key]: e.target.value as string }))
            }
          >
            <MenuItem value="">
              <em>Not set</em>
            </MenuItem>
            {opts.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label || o.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (ft === 'multiline_text' || ft === 'rich_text') {
      const useRich = ft === 'rich_text';
      return (
        <TextField
          key={f.id}
          fullWidth
          sx={{ mb: 2 }}
          label={label}
          value={val}
          multiline
          minRows={useRich ? 6 : 3}
          placeholder={useRich ? 'Long text (plain or HTML, depending on your storefront)' : undefined}
          onChange={(e) => setPlanFeatures((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
        />
      );
    }

    const useMultilineFallback = f.field_key === 'features' || val.length > 120;
    return (
      <TextField
        key={f.id}
        fullWidth
        sx={{ mb: 2 }}
        label={label}
        value={val}
        multiline={useMultilineFallback}
        minRows={useMultilineFallback ? 3 : 1}
        onChange={(e) => setPlanFeatures((prev) => ({ ...prev, [f.field_key]: e.target.value }))}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 280 },
    {
      field: 'category',
      headerName: 'Category',
      width: 130,
      valueGetter: (params: any) => getCategoryName(params.row?.category_id),
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      width: 130,
      valueGetter: (params: any) => getSubcategoryName(params.row?.subcategory_id),
    },
    {
      field: 'service_type_code',
      headerName: 'Service type',
      width: 200,
      valueGetter: (params: any) => serviceTypeLabel(params.row?.service_type_code),
    },
    { field: 'display_order', headerName: 'Display Order', width: 110, type: 'number' },
    {
      field: 'customer_type',
      headerName: 'Residential/Business',
      width: 130,
      valueGetter: (params: any) => {
        const pf = params.row?.plan_features;
        const v = (pf && typeof pf === 'object' && pf.customer_type) || params.row?.customer_type;
        if (!v) return '—';
        return String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase();
      },
    },
    { field: 'sku', headerName: 'SKU', width: 120 },
    { field: 'monthly_price', headerName: 'Monthly', width: 100, renderCell: (params: any) => formatPrice(params.value) },
    { field: 'setup_price', headerName: 'Setup', width: 100, renderCell: (params: any) => formatPrice(params.value) },
    { field: 'one_time_price', headerName: 'One-Time', width: 100, renderCell: (params: any) => formatPrice(params.value) },
    { field: 'is_active', headerName: 'Active', width: 80, type: 'boolean' },
    { field: 'is_available', headerName: 'Available', width: 100, type: 'boolean' },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, renderCell: (params: any) => (
      <Box>
        <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(params.row.id)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    )},
  ];
  
  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Product Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Product
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && products.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategoryId}
                label="Filter by Category"
                onChange={(e) => {
                  setFilterCategoryId(e.target.value);
                  setFilterSubcategoryId('');
                }}
              >
                <MenuItem value="">All categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Subcategory</InputLabel>
              <Select
                value={filterSubcategoryId}
                label="Filter by Subcategory"
                onChange={(e) => setFilterSubcategoryId(e.target.value)}
                disabled={!filterCategoryId && filterSubcategoryOptions.length === 0}
              >
                <MenuItem value="">All subcategories</MenuItem>
                {filterSubcategoryOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>
              {products.length === 0 ? 'No products found' : 'No products match the selected filters'}
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filteredProducts}
            columns={columns}
            paginationModel={{ page: 0, pageSize: 25 }}
            onPaginationModelChange={() => {}}
            pageSizeOptions={[25, 50, 100]}
            checkboxSelection={false}
            autoHeight
            getRowId={(row) => row.id || String(Math.random())}
          />
        )}
      </Paper>
      
      {/* Product Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Edit Product' : 'Create Product'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <FormSection title="Product details">
              <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                helperText="URL-friendly identifier (e.g., 'nbn-100-50')"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SKU"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              />
            </Grid>

            
              </Grid>
            </FormSection>

            <FormSection title="Catalogue">
              <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => {
                    const id = e.target.value as string;
                    const derivedType = deriveProductTypeFromCategoryId(id) ?? formData.product_type;
                    setPlanFeaturesJsonNoSubcategory(JSON.stringify(planFeatures, null, 2));
                    setFormData({
                      ...formData,
                      category_id: id,
                      subcategory_id: '',
                      terms_subcategory_id: '',
                      product_type: derivedType,
                    });
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Category and subcategory are from Category Management. Product type follows the category slug (Internet/Mobile → internet plan, Equipment, Voice).
                </Typography>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subcategory</InputLabel>
                <Select
                  value={formData.subcategory_id}
                  label="Subcategory"
                  onChange={(e) => {
                    const nextId = e.target.value as string;
                    if (formData.subcategory_id && !nextId) {
                      setPlanFeaturesJsonNoSubcategory(JSON.stringify(planFeatures, null, 2));
                    }
                    if (!formData.subcategory_id && nextId) {
                      const parsed = parsePlanFeaturesJson(planFeaturesJsonNoSubcategory);
                      if (parsed) setPlanFeatures(parsed);
                    }
                    setFormData({ ...formData, subcategory_id: nextId });
                  }}
                  disabled={!formData.category_id}
                >
                  <MenuItem value="">None</MenuItem>
                  {subcategories.map((subcat) => (
                    <MenuItem key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </MenuItem>
                  ))}
                </Select>
                {formData.category_id && subcategories.length > 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Subcategories for this category: {subcategories.map((s) => s.name).join(', ')}
                  </Typography>
                ) : null}
              </FormControl>
            </Grid>

            {formData.category_id && subcategories.length > 0 ? (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Terms PDF (customer approval)</InputLabel>
                  <Select
                    value={formData.terms_subcategory_id}
                    label="Terms PDF (customer approval)"
                    onChange={(e) =>
                      setFormData({ ...formData, terms_subcategory_id: e.target.value as string })
                    }
                  >
                    <MenuItem value="">Default (this product&apos;s subcategory)</MenuItem>
                    {subcategories.map((subcat) => (
                      <MenuItem key={subcat.id} value={subcat.id}>
                        {subcat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Upload PDFs per subcategory under Settings → Ordering → Catalogue terms. Choose another
                    subcategory here when the legal terms live under a different catalogue group than the product card.
                  </Typography>
                </FormControl>
              </Grid>
            ) : null}
            
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>CRM service type</InputLabel>
                <Select
                  value={formData.service_type_code}
                  label="CRM service type"
                  onChange={(e) => setFormData({ ...formData, service_type_code: e.target.value as string })}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {serviceTypes.map((t: any) => (
                    <MenuItem key={t.id} value={t.code}>
                      {t.name} ({t.code})
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  From Settings → Service Types. Links catalog products to CRM service lines (e.g. mobile orders).
                </Typography>
              </FormControl>
            </Grid>
            
            
              </Grid>
            </FormSection>

            {formData.service_type_code === 'supa_networks' && (
              <FormSection title="Supa Networks wholesale plan">
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Link this product to a Supa Networks wholesale plan. The Tariff ID is sent to the Supa API when provisioning orders.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap', mb: supaPlansError ? 0 : 0 }}>
                    <FormControl size="small" sx={{ minWidth: 320 }}>
                      {/* shrink keeps label floated above when value is empty string */}
                      <InputLabel shrink>Supa wholesale plan</InputLabel>
                      <Select
                        value={formData.supa_tariff_id}
                        label="Supa wholesale plan"
                        notched
                        displayEmpty
                        onChange={(e) => setFormData({ ...formData, supa_tariff_id: e.target.value as string })}
                      >
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {supaPlans.map((p) => (
                          <MenuItem key={p.tariffID} value={p.tariffID}>
                            {p.planName} — ${p.monthlyFee}/mo (ID: {p.tariffID})
                          </MenuItem>
                        ))}
                        {supaPlans.length === 0 && formData.supa_tariff_id && (
                          <MenuItem value={formData.supa_tariff_id}>
                            ID: {formData.supa_tariff_id} — click &quot;Load plans&quot; to see name
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.preventDefault(); handleLoadSupaPlans(); }}
                      disabled={supaPlansLoading}
                      startIcon={supaPlansLoading ? <CircularProgress size={14} /> : undefined}
                      sx={{ height: 40, whiteSpace: 'nowrap' }}
                    >
                      {supaPlansLoading ? 'Loading…' : 'Load plans from Supa'}
                    </Button>
                  </Box>
                  {supaPlansError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>{supaPlansError}</Alert>
                  )}
                  {!supaPlansError && supaPlans.length > 0 && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                      {supaPlans.length} plans loaded.
                    </Typography>
                  )}
                  {formData.supa_tariff_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Tariff ID: <strong>{formData.supa_tariff_id}</strong>
                    </Typography>
                  )}
              </FormSection>
            )}

            <FormSection title="Pricing">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Retail price (inc GST)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Monthly Price"
                    type="number"
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Setup Price"
                    type="number"
                    value={formData.setup_price}
                    onChange={(e) => setFormData({ ...formData, setup_price: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="One-Time Price"
                    type="number"
                    value={formData.one_time_price}
                    onChange={(e) => setFormData({ ...formData, one_time_price: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
              </Grid>

              {isAdmin && (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cost (ex GST)
                    </Typography>
                    <Chip size="small" label="Admin only" variant="outlined" />
                  </Box>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Monthly Cost"
                        value={costInput}
                        onChange={(e) => {
                          setCostInput(e.target.value);
                          setCostError(null);
                        }}
                        disabled={costLoading}
                        error={!!costError}
                        helperText={costError || 'What this product costs you each month, excluding GST.'}
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Margin (monthly)
                      </Typography>
                      {marginPreview ? (
                        <Typography variant="body2" sx={{ color: marginPreview.margin < 0 ? 'error.main' : 'text.primary' }}>
                          Retail ex GST {formatPrice(marginPreview.retailEx)} &middot; Margin{' '}
                          <strong>
                            {formatPrice(marginPreview.margin)}
                            {marginPreview.pct != null ? ` (${marginPreview.pct.toFixed(2)}%)` : ''}
                          </strong>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Enter a monthly price and cost to see the margin.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
            </FormSection>

            {formData.category_id ? (
              <FormSection title="Product card fields">
                            {storedKeysOutsideDefinition.length > 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Some stored values are not in the current field list (they remain saved):{' '}
                    <code>{storedKeysOutsideDefinition.join(', ')}</code>. Use Extra keys to edit them.
                  </Alert>
                ) : null}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Product card fields (<code>plan_features</code>)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Configure definitions under Orders → Categories → Product Fields (category defaults or per
                  subcategory). Public pages read these values first, then fall back to legacy columns when a key is
                  missing.
                </Typography>
                {productFieldsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Loading field definitions…
                    </Typography>
                  </Box>
                ) : null}
                {!productFieldsLoading && productFieldDefinitions.length === 0 ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No field definitions for this category (and subcategory, if selected). Add them in Category
                      Management, or edit the full JSON below.
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      label="Plan features (JSON)"
                      value={planFeaturesJsonNoSubcategory}
                      onChange={(e) => setPlanFeaturesJsonNoSubcategory(e.target.value)}
                      onBlur={() => {
                        const p = parsePlanFeaturesJson(planFeaturesJsonNoSubcategory);
                        if (p) setPlanFeatures(p);
                      }}
                      InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
                    />
                  </>
                ) : (
                  <>
                    {!productFieldsLoading && productFieldDefinitions.map((f) => renderProductFieldInput(f))}
                    <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Extra keys (JSON object)</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Keys not in the current field list are stored here. Non-string values are coerced to strings
                          on save.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          value={planFeaturesExtrasJson}
                          onFocus={() => {
                            extrasJsonEditorFocused.current = true;
                          }}
                          onBlur={handlePlanFeaturesExtrasBlur}
                          onChange={(e) => handlePlanFeaturesExtrasJsonChange(e.target.value)}
                          InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </>
                )}
              </FormSection>
            ) : null}

            <FormSection title="Media & documents">
            
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Product image
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Optional image for this product (plan cards on the order page, etc.). PNG, JPG, WebP, or GIF; max 2&nbsp;MB.
              </Typography>
              {editingProduct?.image_path ? (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50', maxWidth: 280 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Current image
                  </Typography>
                  <Box
                    component="img"
                    key={`${editingProduct.id}-${editingProduct.image_path}-${productImagePreviewKey}`}
                    src={productImagePublicUrl(editingProduct.id, productImagePreviewKey)}
                    alt=""
                    sx={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                  />
                </Paper>
              ) : !editingProduct ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Save the product first, then edit it to upload an image (or choose a file below and save — image will upload after create).
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No image on file.
                </Typography>
              )}
              <input
                ref={productImageInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                onChange={handleProductImageFileChange}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => productImageInputRef.current?.click()}
                  disabled={productImageUploading}
                >
                  {productImageFile ? productImageFile.name : 'Choose image'}
                </Button>
                {editingProduct && productImageFile ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={productImageUploading}
                    onClick={handleUploadProductImageNow}
                  >
                    {productImageUploading ? 'Uploading…' : 'Upload now'}
                  </Button>
                ) : null}
                {editingProduct?.image_path ? (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={productImageUploading}
                    onClick={handleRemoveProductImage}
                  >
                    Remove image
                  </Button>
                ) : null}
              </Box>
              {productImageFile && !editingProduct ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Image will be uploaded when you click Create.
                </Typography>
              ) : null}
            
              {showCisUpload && (
                <>
                  <Divider sx={{ my: 2.5 }} />
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Critical Information Summary (PDF)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Shown on the order page under “Select plan” when a PDF is uploaded.
                </Typography>
                {editingProduct?.critical_info_summary_path && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current: <strong>PDF uploaded.</strong> Choose a new file to replace.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        File: {editingProduct.critical_info_summary_path}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Button variant="outlined" component="label" size="small">
                  {cisFile ? cisFile.name : 'Choose PDF'}
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    hidden
                    onChange={(e) => setCisFile(e.target.files?.[0] ?? null)}
                  />
                </Button>
                </>
              )}
            </FormSection>

            <FormSection title="Availability & display">
              <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_available}
                    onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                  />
                }
                label="Available"
              />
            </Grid>

              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.slug}>
            {editingProduct ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductsPage;

